import React from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, ArrowRight, FileText, BookOpen } from 'lucide-react';
import { fadeInUp } from '../hooks/useScrollAnimation';
export function HeroSection() {
  return <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20" data-id="element-248">
      {/* Background Gradient Mesh — Circuit board glow */}
      <div className="absolute inset-0 z-0" data-id="element-249">
        <div className="absolute top-[-10%] left-[-5%] w-[55%] h-[60%] rounded-full bg-cyan-900/20 blur-[140px]" data-id="element-250" />
        <div className="absolute bottom-[-10%] right-[-5%] w-[50%] h-[55%] rounded-full bg-blue-900/30 blur-[120px]" data-id="element-251" />
        <div className="absolute top-[30%] right-[20%] w-[30%] h-[30%] rounded-full bg-cyan/5 blur-[80px]" data-id="element-252" />
      </div>

      {/* Circuit node overlay */}
      <div className="absolute inset-0 z-0 opacity-[0.06]" style={{
      backgroundImage: `
            radial-gradient(circle, #06B6D4 1.5px, transparent 1.5px),
            linear-gradient(rgba(6,182,212,0.3) 1px, transparent 1px),
            linear-gradient(90deg, rgba(6,182,212,0.3) 1px, transparent 1px)
          `,
      backgroundSize: '60px 60px, 60px 60px, 60px 60px'
    }} data-id="element-253" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 w-full" data-id="element-254">
        <motion.div initial="hidden" animate="visible" variants={fadeInUp} className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-255">
          {/* Left — Text Content */}
          <div className="max-w-2xl" data-id="element-256">
            <div className="inline-flex items-center gap-3 mb-8 px-4 py-2 border border-gold/30 bg-gold/5 rounded-full" data-id="element-257">
              <span className="w-2 h-2 rounded-full bg-gold animate-pulse" data-id="element-258"></span>
              <span className="font-sans text-xs md:text-sm text-gold tracking-widest uppercase font-semibold" data-id="element-259">
                Enterprise Technology Executive · Open to Opportunities
              </span>
            </div>

            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-8 tracking-tight" data-id="element-260">
              Engineering Resilience at{' '}
              <span className="text-white opacity-70" data-id="element-261">Enterprise Scale</span>
            </h1>

            <div className="mb-10" data-id="element-262">
              <p className="text-lg md:text-xl text-gray-300 font-light leading-relaxed" data-id="element-263">
                30+ years leading large-scale infrastructure, cloud, and
                platform engineering in highly regulated financial services.
                Seeking CTO, board, and executive advisory roles.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row flex-wrap gap-4" data-id="element-264">
              <a href="#career-timeline" className="px-8 py-4 bg-white text-dark-bg font-bold rounded hover:bg-gray-200 transition-all flex items-center justify-center gap-2 group" data-id="element-265">
                View Career History
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-266" />
              </a>
              <a href="https://heyzine.com/flip-book/e2c9bcea18.html" target="_blank" rel="noopener noreferrer" className="px-8 py-4 bg-transparent border border-gold/50 text-gold font-medium rounded hover:bg-gold/10 transition-all flex items-center justify-center gap-2" data-id="element-267">
                <FileText className="w-4 h-4" data-id="element-268" />
                View Executive Resume
              </a>
              <a href="https://heyzine.com/flip-book/e53f06ab30.html" target="_blank" rel="noopener noreferrer" className="px-8 py-4 bg-transparent border border-slate-600 text-slate-300 font-medium rounded hover:border-slate-400 hover:text-white transition-all flex items-center justify-center gap-2" data-id="element-269">
                <BookOpen className="w-4 h-4" data-id="element-270" />
                Leadership Philosophy
              </a>
            </div>
          </div>

          {/* Right — Headshot */}
          <motion.div initial={{
          opacity: 0,
          x: 40
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.8,
          delay: 0.3
        }} className="hidden lg:flex justify-center items-center" data-id="element-271">
            <div className="relative" data-id="element-272">
              {/* Gold border frame */}
              <div className="absolute -inset-1 rounded-2xl bg-gradient-to-br from-gold/40 via-gold/10 to-transparent" data-id="element-273"></div>
              <div className="relative w-80 xl:w-96 aspect-[4/5] rounded-2xl overflow-hidden border border-gold/30" data-id="element-274">
                <img src="https://cdn.magicpatterns.com/uploads/dmJTA2YsaWCaEwYeBYJdDH/Michael_Parks_284x210_1.jpg" alt="Michael Parks — Enterprise Technology Executive" className="w-full h-full object-cover object-center scale-110" data-id="element-275" />
                {/* Subtle overlay gradient at bottom */}
                <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-dark-bg/60 to-transparent" data-id="element-276"></div>
              </div>
              {/* Name badge overlay */}
              <div className="absolute bottom-4 left-4 right-4 bg-dark-bg/80 backdrop-blur-sm border border-dark-border rounded-lg px-4 py-3" data-id="element-277">
                <div className="text-white font-semibold text-sm" data-id="element-278">
                  Michael Parks
                </div>
                <div className="text-gold/80 text-xs mt-0.5" data-id="element-279">
                  Chief Technology Officer
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>

      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 1.5,
      duration: 1
    }} className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce text-gray-600" data-id="element-280">
        <ChevronDown className="w-6 h-6" data-id="element-281" />
      </motion.div>
    </section>;
}